import React, { useCallback, useEffect, useRef, useState } from "react";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  LeaderBoardList,
  LeaderboardGroup,
} from "@nestcoinco/hyper-api-gateway-api-client";

import "./LeaderboardTab.scss";
import HyperCoin from "../../HyperCoin/HyperCoin";
import ImageWarning from "../../../images/warning-alert-circle-orange.svg";
import { formatCurrency } from "../../../utils/helpers";
import { handleGetLeaderboard } from "../../../api-clients/tournamentLeaderboardClient";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import useAuth from "../../../hooks/useAuth";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

enum LeaderboardTabs {
  LAST_7_DAYS = LeaderboardGroup.LAST_7_DAYS,
  LAST_30_DAYS = LeaderboardGroup.LAST_30_DAYS,
  ALL_TIME = LeaderboardGroup.ALL_TIME,
}

function getLeaderboardFilter(tab: LeaderboardTabs): LeaderboardGroup | null {
  switch (tab) {
    case LeaderboardTabs.LAST_7_DAYS:
      return LeaderboardGroup.LAST_7_DAYS;
    case LeaderboardTabs.LAST_30_DAYS:
      return LeaderboardGroup.LAST_30_DAYS;
    case LeaderboardTabs.ALL_TIME:
      return LeaderboardGroup.ALL_TIME;
    default:
      return null;
  }
}

function useTabHeading() {
  const { t } = useTranslation("games");

  const getTabHeading = useCallback(
    (tab: LeaderboardTabs) => {
      switch (tab) {
        case LeaderboardTabs.LAST_7_DAYS:
          return t("last_number_days", { count: 7 });
        case LeaderboardTabs.LAST_30_DAYS:
          return t("last_number_days", { count: 30 });
        case LeaderboardTabs.ALL_TIME:
          return t("all_time");
        default:
          return "";
      }
    },
    [t]
  );

  return { getTabHeading };
}

function LeaderboardTab() {
  const [activeTab, setActiveTab] = useState<LeaderboardTabs>(
    LeaderboardTabs.LAST_7_DAYS
  );
  const [isMyItemVisible, setIsMyItemVisible] = useState(false);
  const [leaderboardList, setLeaderboardList] = useState<LeaderBoardList>();
  const [isLoading, setIsLoading] = useState(false);
  const loggedInUserRef = useRef<HTMLDivElement>(null);
  const { userProfile } = useAuth();
  const { t } = useTranslation("games");
  const { getTabHeading } = useTabHeading();
  const { isFiatDisplay } = useHprFiatFormatter();

  const storedUsername = userProfile?.username;

  const loggedInUserItem = leaderboardList?.records.find(
    (item) => item.username === storedUsername
  );

  useIntersectionObserver({
    targetRef: loggedInUserRef,
    options: { threshold: 0.9 },
    callback: ([entry]) => setIsMyItemVisible(entry.isIntersecting),
  });

  useEffect(() => {
    const filter = getLeaderboardFilter(activeTab);
    if (!filter) return;
    setIsLoading(true);

    const fetchLeaderboard = async () => {
      const leaderboard = await handleGetLeaderboard({ type: filter });
      setLeaderboardList(leaderboard);
      setIsLoading(false);
    };

    fetchLeaderboard();
  }, [activeTab]);

  return (
    <div className="leaderboard-tab-container">
      <div className="total-gamers-container">
        <p className="heading mb-2">{t("total_gamers")}</p>
        <p className="content">{leaderboardList?.totalItems || "--"}</p>
      </div>
      <p role="alert" className="warning d-flex align-items-center">
        <Image src={ImageWarning} alt="warning icon" height={11} />
        {isFiatDisplay ? (
          <span>{t("leaderboard_tab_warning_fiat")}</span>
        ) : (
          <span>
            {t("you_do_not_win")} <HyperCoin height={11} />{" "}
            {t("hpr_for_topping_this_leaderboard")}{" "}
          </span>
        )}{" "}
      </p>
      <div className="leaderboard-tab">
        <div className="d-flex px-3 justify-content-between mb-2 py-2">
          {Object.values(LeaderboardTabs).map((tab) => (
            <button
              key={tab}
              className={`tab-heading ${activeTab === tab ? "active-tab" : ""}`}
              onClick={() => setActiveTab(tab as LeaderboardTabs)}
            >
              {getTabHeading(tab as LeaderboardTabs)}
            </button>
          ))}
        </div>
        <div className="d-flex justify-content-between px-3 mb-2">
          <div className="gap-12 d-flex header-text">
            <span>{t("serial_number")}</span>
            <span>{t("player_username")}</span>
          </div>
          <p className="header-text">{t("score")}</p>
        </div>
        {isLoading && <CustomSpinner size="sm" className="spinner" />}
        <div className="gap-12 d-flex flex-column pb-4">
          {leaderboardList?.records.map(
            ({ rank, avatarUrl, score, username }, i) => {
              const isLoggedInUser = storedUsername === username;
              return (
                <div
                  key={username + i}
                  ref={isLoggedInUser ? loggedInUserRef : undefined}
                  className={`d-flex justify-content-between align-items-center px-3 py-1 ${
                    isLoggedInUser ? "highlight" : ""
                  }`}
                >
                  <div className="gap-12 d-flex align-items-center">
                    <span className="serial-no">{rank}</span>
                    <span className="avatar-circle">
                      <Image src={avatarUrl} alt="" />
                    </span>
                    <span className="username">
                      {isLoggedInUser ? t("you") : `@${username}`}
                    </span>
                  </div>
                  <p className="score">{formatCurrency(score)}</p>
                </div>
              );
            }
          )}
          {!isMyItemVisible && loggedInUserItem && (
            <div className="fixed-item position-fixed">
              <div className="MobileOnly highlight d-flex justify-content-between align-items-center px-3">
                <div className="gap-12 d-flex align-items-center">
                  <span className="serial-no">{loggedInUserItem.rank}</span>
                  <span className="avatar-circle">
                    <Image src={loggedInUserItem.avatarUrl} alt="" />
                  </span>
                  <span className="username fw-bold">{t("you")}</span>
                </div>
                <p className="score">
                  {formatCurrency(loggedInUserItem.score)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeaderboardTab;
